package dev.lingvo.site.pages.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import dev.lingvo.shared.ScreenshotPublicReviewDto
import dev.lingvo.shared.ScreenshotPublicReviewImageDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.url.URL

/** No app layout: a bearer link reveals only frozen render images and their captions. */
@Page("/review/screenshots")
@Composable
fun PublicScreenshotReviewPage() {
    var token by remember { mutableStateOf(consumeReviewFragment()) }
    var generation by remember { mutableStateOf(0) }
    var review by remember { mutableStateOf<ScreenshotPublicReviewDto?>(null) }
    var urls by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var unavailable by remember { mutableStateOf(token == null) }
    var loading by remember { mutableStateOf(token != null) }
    val visibilityWake = remember { Channel<Unit>(Channel.CONFLATED) }
    DisposableEffect(Unit) {
        document.title = tr("review.screenshots.title", "Screenshot review")
        val meta = document.createElement("meta").apply { setAttribute("name", "referrer"); setAttribute("content", "no-referrer") }
        val robots = document.createElement("meta").apply { setAttribute("name", "robots"); setAttribute("content", "noindex, nofollow, noarchive") }
        document.head?.appendChild(meta)
        document.head?.appendChild(robots)
        val hashChanged = EventListener {
            val candidate = consumeReviewFragment()
            // Retire publication before cancellation cleanup, including when the same token is reopened.
            generation++
            token = candidate
            review = null
            urls = emptyMap()
            unavailable = candidate == null
            loading = candidate != null
        }
        val visibilityChanged = EventListener {
            if (document.asDynamic().visibilityState != "hidden") visibilityWake.trySend(Unit)
        }
        window.addEventListener("hashchange", hashChanged)
        document.addEventListener("visibilitychange", visibilityChanged)
        onDispose {
            window.removeEventListener("hashchange", hashChanged)
            document.removeEventListener("visibilitychange", visibilityChanged)
            visibilityWake.close()
            meta.parentNode?.removeChild(meta)
            robots.parentNode?.removeChild(robots)
        }
    }
    val requestedToken = token
    val requestedGeneration = generation
    LaunchedEffect(requestedGeneration) {
        val secret = requestedToken ?: return@LaunchedEffect
        if (generation != requestedGeneration) return@LaunchedEffect
        val ownedUrls = mutableMapOf<String, String>()
        fun clear() {
            ownedUrls.values.forEach(URL::revokeObjectURL)
            ownedUrls.clear()
            if (generation == requestedGeneration) {
                urls = emptyMap()
                review = null
            }
        }
        visibilityWake.tryReceive()
        loading = true
        unavailable = false
        try {
            while (true) {
                if (document.asDynamic().visibilityState == "hidden") {
                    visibilityWake.receive()
                    continue
                }
                val current = PublicReviewApi.review(secret)
                if (generation != requestedGeneration) return@LaunchedEffect
                val ids = current.images.map { it.id }.toSet()
                (ownedUrls.keys - ids).forEach { id -> ownedUrls.remove(id)?.let(URL::revokeObjectURL) }
                review = current
                for (image in current.images) {
                    if (image.id !in ownedUrls) {
                        val blob = PublicReviewApi.image(secret, image.id)
                        if (generation != requestedGeneration) return@LaunchedEffect
                        ownedUrls[image.id] = URL.createObjectURL(blob)
                    }
                    urls = ownedUrls.toMap()
                }
                loading = false
                // One sequential reader handles both visibility return and the visible heartbeat.
                withTimeoutOrNull(30_000) { visibilityWake.receive() }
            }
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) {
            if (generation == requestedGeneration) { clear(); unavailable = true; loading = false }
        }
        finally { clear() }
    }
    Column(Modifier.gap(Space.LG).styleModifier {
        property("width", "100%"); property("max-width", "1100px"); property("margin", "0 auto"); property("padding", "24px")
        property("box-sizing", "border-box"); property("min-width", "0"); property("overflow-wrap", "anywhere")
    }) {
        H1 { Text(t("review.screenshots.title", "Screenshot review")) }
        LMuted(t("review.screenshots.read_only", "Read-only previews. This link cannot edit or approve screenshots."))
        if (unavailable) {
            LAlert(t("review.screenshots.unavailable", "This review link is unavailable. It may have expired or been revoked. Check your connection or ask the link's owner for a new link."), variant = LAlertVariant.WARNING)
            if (token != null) LButton(t("review.screenshots.retry", "Retry review"), { generation++ }, variant = LButtonVariant.SECONDARY)
        }
        if (loading) LSpinner(label = t("review.screenshots.loading", "Loading screenshot previews"))
        review?.let { PublicReviewGallery(it.images, urls) }
    }
}

/** Consume every fragment ingress, including replacements while this route remains mounted. */
private fun consumeReviewFragment(): String? {
    val candidate = publicReviewToken(window.location.hash)
    window.history.replaceState(null, "", window.location.pathname)
    return candidate
}

/** Captions and locale labels are plain text; image sources are local object URLs from scoped fetches. */
@Composable
internal fun PublicReviewGallery(images: List<ScreenshotPublicReviewImageDto>, urls: Map<String, String>) {
    Column(Modifier.gap(Space.XL).styleModifier { property("min-width", "0"); property("width", "100%") }) {
        images.forEach { image ->
            TagElement<HTMLElement>("figure", applyAttrs = { style { property("margin", "0"); property("min-width", "0"); property("overflow-wrap", "anywhere") } }) {
                val label = tf("review.screenshots.image_label", "{language} · {device}", "language" to localeInfo(image.locale).englishName,
                    "device" to image.deviceClass.displayName)
                urls[image.id]?.let { source -> Img(source, alt = image.caption ?: label, attrs = {
                    attr("width", image.width.toString()); attr("height", image.height.toString()); attr("referrerpolicy", "no-referrer")
                    style { property("display", "block"); property("width", "auto"); property("max-width", "100%"); property("max-height", "70vh"); property("height", "auto"); property("object-fit", "contain") }
                }) }
                TagElement<HTMLElement>("figcaption", applyAttrs = { attr("lang", image.locale); style { property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere") } }) {
                    Text(label)
                    image.caption?.let { Text("\n$it") }
                }
            }
        }
    }
}
